import JobCardBilling from "@/components/JobCardBilling";
import { JobFinancialWorkspace } from "@/components/job/JobFinancialWorkspace";
import { FieldJobOverview } from "@/components/job/FieldJobOverview";
import { displayDate } from "@/lib/dates";
import { JobHazardousSubstances } from "./HazardousSubstances";
import SiteAudit from '@/components/SiteAudit';
import JobSchedule from "@/components/JobSchedule";
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "./_shared";
import {
  ArrowLeft, Trash2, Edit3, Briefcase, Calendar, Users, FileDown, MoreHorizontal, Receipt, AlertTriangle,
  LayoutDashboard, Hammer, HardHat, Wallet, FolderOpen, ShieldCheck, MessagesSquare, History, Boxes,
  Archive, ArchiveRestore,
} from "lucide-react";
import { toast } from "sonner";
import { JobPlans } from "@/components/JobPlans";
import { ClientPortalCard } from "@/components/ClientPortalCard";
import { JobDocuments } from "@/components/JobDocuments";
import { JobVariations } from "@/components/JobVariations";
import { JobTasksPanel } from "@/components/JobTasksPanel";
import { JobListsPanel } from "@/components/JobListsPanel";
import { JobPhotos } from "@/components/JobPhotos";
import { JobSSSP } from "@/components/JobSSSP";
import { JobSiteMaterialsPanel } from "@/components/JobSiteMaterialsPanel";
import { JobActivity } from "@/components/JobActivity";
import { JobSigninPanel } from "@/components/JobSigninPanel";
import { JobForms } from "@/components/JobForms";
import { JobDiaryWorkspace } from "@/components/job/JobDiaryWorkspace";
import { JOB_ANCHOR_ROUTES, jobDestination } from "@/components/job/jobNavigation";
import { DrawingsTab } from "@/components/pm/DrawingsTab";
import { PunchListTab } from "@/components/pm/PunchListTab";
import { PhotosTab } from "@/components/pm/PhotosTab";
import { JobCommunications } from "@/components/JobCommunications";

import { JobAddMenu, jobWorkflow } from "@/components/job/JobWorkflow";
import { JobOverviewV2 } from "@/components/job/JobOverviewV2";
import { JobSiteMap } from "@/components/job/JobSiteMap";
import "@/components/job/jobOverviewV2.css";
import { Map as MapIcon, FileText, Users2, CalendarRange, CalendarDays as CalDays, UserRound, MapPin as Pin } from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", Icon: LayoutDashboard, description: "What needs attention and where the job stands." },
  { key: "work", label: "Daily activity", Icon: Hammer, description: "Today's tasks, site diary, photos and who's on site.", children: [
    { key: "work", label: "Tasks" }, { key: "diary", label: "Site diary" }, { key: "photos", label: "Photos" },
    { key: "needs", label: "Needs & handover" }, { key: "crew", label: "Crew & plant" }, { key: "punch", label: "Defects", pm: true },
  ] },
  { key: "materials", label: "Records", Icon: FolderOpen, description: "Materials, files, drawings, safety and history.", children: [
    { key: "materials", label: "Materials" }, { key: "documents", label: "Files & plans" }, { key: "drawings", label: "Drawings", pm: true },
    { key: "safety", label: "Safety & forms" }, { key: "communications", label: "Messages", office: true }, { key: "activity", label: "History" },
  ] },
  { key: "costs", label: "Financials", Icon: Wallet, office: true, description: "Budget, spending and customer billing.", children: [
    { key: "costs", label: "Costs & budget" }, { key: "commercial", label: "Variations & billing" }, { key: "supplier-bills", label: "Supplier bills", pm: true },
  ] },
  { key: "map", label: "Map", Icon: MapIcon, shortcut: true, description: "Full-size map of the job site with directions." },
];

const SHORTCUT_KEYS = ["documents", "crew", "map"];

export default function JobDetail() {
  const { id, tab } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const [safetyTypes, setSafetyTypes] = useState(null);
  useEffect(() => {
    api.get("/settings/safety-form-types").then(r => setSafetyTypes(r.data.types || {})).catch(() => {});
  }, []);
  const { user } = useAuth();
  const isOffice = user.role !== "field";
  const canProjectControl = ["admin", "office", "super_admin"].includes(user.role);
  const canCommercial = ["admin", "office", "super_admin", "quantity_surveyor"].includes(user.role);

  const [job, setJob] = useState(null);
  const [showProjectPhotos, setShowProjectPhotos] = useState(location.hash === "#project-photos");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packBusy, setPackBusy] = useState(false);
  const [unbilledWarn, setUnbilledWarn] = useState(null);

  const base = `/app/jobs/${id}`;
  const tabs = TABS.filter(t => !t.office || canCommercial).map(t => ({ ...t, children: t.children?.filter(child => (!child.pm || canProjectControl) && (!child.office || isOffice)) }));
  const routeKeys = tabs.flatMap(t => t.children ? t.children.map(child => child.key) : [t.key]);
  const active = routeKeys.includes(tab) ? tab : "overview";
  const activeGroup = tabs.find(t => t.key === active || t.children?.some(child => child.key === active));
  // Keep navigation in this full workspace for organisations with a simplified Jobs view.
  const advanced = true;
  const jobLink = (name, anchor = "") => jobDestination(id, name, { anchor, advanced });

  useEffect(() => {
    if (loading || !job || !location.hash) return;
    if (location.hash === "#project-photos") setShowProjectPhotos(true);
    const target = document.getElementById(location.hash.slice(1));
    if (target) { target.scrollIntoView({ block: "start" }); target.focus({ preventScroll: true }); }
  }, [location.pathname, location.hash, active, loading, job]);

  // Preserve old bookmarks into sections that now have a single dedicated home.
  useEffect(() => {
    const moved = JOB_ANCHOR_ROUTES[location.hash.slice(1)];
    if (moved && moved !== tab && routeKeys.includes(moved)) {
      nav({ pathname: `${base}/${moved}`, search: location.search, hash: location.hash }, { replace: true });
    } else if (tab && !routeKeys.includes(tab)) {
      nav({ pathname: base, search: advanced ? "?view=advanced" : "" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, location.hash, location.search, isOffice, canProjectControl]);

  const downloadPack = async () => {
    setPackBusy(true);
    try {
      const res = await api.get(`/jobs/${id}/completion-pack.pdf`, { responseType: "blob", timeout: 120000 });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `completion-pack-${job?.job_number || id}.pdf`;
      a.click();
      toast.success("Completion pack downloaded");
    } catch { toast.error("Couldn't build the completion pack"); }
    finally { setPackBusy(false); }
  };

  const load = async () => {
    try { const { data } = await api.get(`/jobs/${id}`); setJob(data); }
    catch (e) { toast.error("Job not found"); nav("/app/jobs"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    if (isOffice) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  const remove = async () => {
    if (!window.confirm("Delete this job? Documents and chat will be removed.")) return;
    try { await api.delete(`/jobs/${id}`); toast.success("Job deleted"); nav("/app/jobs"); }
    catch (e) { toast.error("Failed"); }
  };

  const setStatus = async (status) => {
    if (status === "completed" && job.billing_type === "charge_up") {
      try {
        const { data } = await api.get(`/jobs/${id}/charge-up/summary`);
        if (data.unbilled.total > 0.005) { setUnbilledWarn({ status, data }); return; }
      } catch { /* fall through */ }
    }
    try { await api.patch(`/jobs/${id}`, { status }); setJob((j) => ({ ...j, status })); toast.success("Status updated"); }
    catch { toast.error("Failed"); }
  };

  const forceComplete = async () => {
    const status = unbilledWarn.status;
    setUnbilledWarn(null);
    try { await api.patch(`/jobs/${id}`, { status }); setJob((j) => ({ ...j, status })); toast.success("Status updated"); }
    catch { toast.error("Failed"); }
  };

  const archive = async () => {
    if (!window.confirm("Archive this job? It will move to Archived Jobs and drop out of the day-to-day screens. All its history is kept and you can restore it any time.")) return;
    try {
      await api.post(`/jobs/${id}/archive`);
      toast.success("Job archived");
      nav("/app/jobs/archived");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to archive"); }
  };

  const unarchive = async () => {
    try {
      await api.post(`/jobs/${id}/unarchive`);
      setJob((j) => ({ ...j, archived: false }));
      toast.success("Job restored");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to restore"); }
  };

  const userById = (uid) => users.find((u) => u.id === uid);

  if (loading || !job) {
    return (
      <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="job-detail-loading">
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Loading…</div>
      </div>
    );
  }

  const workflow = jobWorkflow(job, user, safetyTypes, advanced);
  const section = "scroll-mt-24 focus:outline-none";

  return (
    <div data-testid={`job-detail-page-${id}`} className="job-dash px-4 lg:px-6 py-4 w-full max-w-[1480px] mx-auto pb-24 lg:pb-8 overflow-x-hidden bg-[#F5F7FA] dark:bg-background min-h-full">
      {/* ===== Cinematic job header ===== */}
      <Link to="/app/jobs" className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-zinc-500 hover:text-[#FF5F15] mb-2" data-testid="job-detail-back"><ArrowLeft className="w-3.5 h-3.5" /> All jobs</Link>
      <section className="jo-hero" data-testid="job-detail-hero">
        <div>
          {job.job_number && <span className="jo-num" data-testid="job-detail-number">{job.job_number}</span>}
          <h1 className="break-words" data-testid="job-detail-title">{job.title}</h1>
          <div className="jo-meta" data-testid="job-detail-meta-strip">
            {job.client_name && <span><UserRound className="w-4 h-4" />{job.client_name}</span>}
            {job.site_address && <span><Pin className="w-4 h-4" />{job.site_address}</span>}
          </div>
        </div>
        <div className="jo-hero-bottom">
          <div className="jo-meta">
            {canProjectControl ? <Select value={job.status} onValueChange={setStatus}>
              <SelectTrigger id="job-status" className={`jo-pill ${job.status} !h-8 !w-auto border-0 gap-2`} data-testid="job-detail-status-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="planned">Planned</SelectItem><SelectItem value="in_progress">In progress</SelectItem><SelectItem value="on_hold">On hold</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent>
            </Select> : <StatusBadge status={job.status} />}
            {job.start_date && <span><CalDays className="w-4 h-4" />Start: {displayDate(job.start_date)}</span>}
            {job.end_date && <span>Due: {displayDate(job.end_date)}</span>}
            {isOffice && <span><Users2 className="w-4 h-4" />{job.assigned_user_ids?.length || 0} with job access</span>}
            {job.archived && <span className="jo-pill completed" data-testid="job-detail-archived-badge"><Archive className="w-3 h-3" /> Archived</span>}
            {isOffice && job.billing_type === "charge_up" && <span className="jo-pill in_progress" data-testid="job-detail-charge-up-badge"><Receipt className="w-3 h-3" /> Charge-Up</span>}
          </div>
          <div className="jo-actions">
            {isOffice ? <JobAddMenu workflow={workflow} /> : active !== "overview" && active !== "materials" && <Button asChild className="min-h-12 bg-[#FF5F15] hover:bg-[#E65100] text-white"><Link to={jobDestination(id, "materials", {action: "material", advanced: true})}><Boxes className="w-4 h-4 mr-2" />Add material</Link></Button>}
            {canProjectControl && <>
              <Link to={`/app/jobs?edit=${job.id}`} className="jo-light whitespace-nowrap" data-testid="job-detail-edit"><Edit3 className="w-4 h-4" /> Edit Job</Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><button type="button" className="jo-light" aria-label="More job actions" data-testid="job-detail-more"><MoreHorizontal className="w-4 h-4" /></button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => nav(`${base}/documents`)}><FileDown className="w-4 h-4 mr-2" /> Completion pack</DropdownMenuItem>
                  {job.archived ? (
                    <DropdownMenuItem onClick={unarchive} data-testid="job-detail-unarchive"><ArchiveRestore className="w-4 h-4 mr-2" /> Restore from archive</DropdownMenuItem>
                  ) : job.status === "completed" ? (
                    <DropdownMenuItem onClick={archive} data-testid="job-detail-archive"><Archive className="w-4 h-4 mr-2" /> Archive job</DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem disabled className="opacity-60" data-testid="job-detail-archive-disabled"><Archive className="w-4 h-4 mr-2" /> Archive (complete job first)</DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={remove} className="text-red-600 focus:text-red-600" data-testid="job-detail-delete"><Trash2 className="w-4 h-4 mr-2" /> Delete job</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>}
          </div>
        </div>
      </section>

      <nav className="sticky top-0 z-20 bg-background border-b border-border mb-4" aria-label="Job workspace" data-testid="job-workspace-nav">
        {!isOffice && <div className="grid grid-cols-3 gap-1 py-2">{["overview", "work", "materials"].map(key => {const item = tabs.find(t => t.key === key); return <Link key={key} to={jobLink(key)} aria-current={activeGroup?.key === key ? "page" : undefined} className={`min-h-14 rounded-lg flex flex-col items-center justify-center gap-1 text-xs font-medium ${activeGroup?.key === key ? "bg-orange-50 text-orange-800 dark:bg-orange-950 dark:text-orange-200" : "text-muted-foreground"}`}><item.Icon className="w-5 h-5" />{key === "overview" ? "Home" : key === "work" ? "Daily" : "Records"}</Link>;})}</div>}
        <div className={`${isOffice ? "flex" : "hidden"} items-center gap-2 py-2`}>
          <select aria-label="Job workspace section" value={activeGroup?.key || "overview"} onChange={event => nav(jobLink(event.target.value))} className="md:hidden min-h-11 flex-1 min-w-0 rounded-md border border-border bg-background px-3 text-sm">
            {tabs.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <div className="hidden md:flex jo-tabs flex-1 min-w-0 !my-0">
            {tabs.filter(t => !t.more && !t.shortcut).map(t => <Link key={t.key} to={jobLink(t.key)} aria-current={activeGroup?.key === t.key && !SHORTCUT_KEYS.includes(active) ? "page" : undefined} data-testid={`job-tab-${t.key}`} className="jo-tab"><t.Icon className="w-4 h-4" />{t.label}</Link>)}
            <Link to={jobLink("documents")} aria-current={active === "documents" ? "page" : undefined} className="jo-tab" data-testid="job-tab-documents-shortcut"><FileText className="w-4 h-4" />Documents</Link>
            {routeKeys.includes("crew") && <Link to={jobLink("crew")} aria-current={active === "crew" ? "page" : undefined} className="jo-tab" data-testid="job-tab-team-shortcut"><Users2 className="w-4 h-4" />Team &amp; Plant</Link>}
            {canProjectControl && <Link to={`/app/crew-planner?job_id=${encodeURIComponent(job.id)}`} className="jo-tab" data-testid="job-tab-schedule-shortcut"><CalendarRange className="w-4 h-4" />Schedule</Link>}
            <Link to={jobLink("map")} aria-current={active === "map" ? "page" : undefined} className="jo-tab" data-testid="job-tab-map"><MapIcon className="w-4 h-4" />Map</Link>
          </div>
          <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" className={`${tabs.some(t => t.more) ? "hidden md:inline-flex" : "hidden"} min-h-11`}>{activeGroup?.more ? activeGroup.label : "More"}<MoreHorizontal className="w-4 h-4 ml-2" /></Button></DropdownMenuTrigger><DropdownMenuContent align="end">{tabs.filter(t => t.more).map(t => <DropdownMenuItem key={t.key} asChild><Link to={jobLink(t.key)}><t.Icon className="w-4 h-4 mr-2" />{t.label}</Link></DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu>
        </div>
      </nav>
      {active !== "overview" && <div className="mb-4 space-y-3"><div><h2 className="text-lg font-semibold">{activeGroup?.label}</h2>{activeGroup?.description && <p className="text-sm text-muted-foreground mt-1">{activeGroup.description}</p>}</div>{activeGroup?.children && activeGroup.key !== "costs" && <nav aria-label={`${activeGroup.label} sections`} className="flex flex-wrap gap-2">{[...new Set(activeGroup.children.map(child => child.group || "Sections"))].map(group => <div key={group} className="flex flex-wrap items-center gap-1">{activeGroup.key === "work" && <span className="text-xs font-semibold text-muted-foreground px-2">{group}</span>}{activeGroup.children.filter(child => (child.group || "Sections") === group).map(child => <Button key={child.key} variant={active === child.key ? "secondary" : "ghost"} asChild className="min-h-11"><Link aria-current={active === child.key ? "page" : undefined} to={jobLink(child.key)}>{child.label}</Link></Button>)}</div>)}</nav>}</div>}

      {/* ===== Active tab content ===== */}
      <div className="min-w-0" data-testid={`job-tabpanel-${active}`}>
        {isOffice && active === 'work' && <details className="mb-4 rounded-lg border border-border bg-card p-3"><summary className="cursor-pointer font-medium text-sm">Planning · upcoming people &amp; plant</summary><div className="mt-3"><JobSchedule jobId={job.id}/></div></details>}
        {active === "overview" && (isOffice ? <JobOverviewV2 key={job.id} job={job} workflow={workflow} /> : <FieldJobOverview job={job} />)}
        {active === "map" && <JobSiteMap job={job} />}

        {active === "work" && (
          <div className="space-y-3.5">
            <JobTasksPanel jobId={job.id} />
            {!isOffice && <div id="job-variations" tabIndex={-1} className={section}><JobVariations job={job} /></div>}
          </div>
        )}

        {active === "needs" && <div id="job-needs" tabIndex={-1} className={section}><JobListsPanel key={job.id} jobId={job.id} /></div>}
        {active === "diary" && <div id="job-diary" tabIndex={-1} className={section}><JobDiaryWorkspace key={job.id} job={job} /></div>}
        {active === "materials" && <div id="job-materials" tabIndex={-1} className={`space-y-3 ${section}`}><p className="text-sm text-muted-foreground">Use Add material to choose from the company catalogue and record yard stock or a supplier delivery. All material records for this job appear here.</p><JobSiteMaterialsPanel jobId={job.id} showWorkspaceLink={isOffice} /><div className="flex flex-wrap gap-3">{workflow.materialChoices.filter(item => ["material-cost"].includes(item.key)).map(item => <Button key={item.key} variant="outline" asChild className="min-h-11"><Link to={item.to}>{item.label}</Link></Button>)}{canProjectControl && <Link to={jobLink("supplier-bills")} className="inline-flex items-center min-h-11 text-sm text-orange-700 dark:text-orange-300">Review supplier bills →</Link>}</div></div>}
        {active === "photos" && <div id="job-photos" tabIndex={-1} className={`space-y-4 ${section}`}><JobPhotos jobId={job.id} />{canProjectControl && <details id="project-photos" tabIndex={-1} open={showProjectPhotos} onToggle={event => setShowProjectPhotos(event.currentTarget.open)} className="border-t border-border pt-3"><summary className="cursor-pointer py-2 text-sm font-medium">Project photo library</summary><p className="text-sm text-muted-foreground my-3">Photos organised in Project Control, including their existing captions and records.</p>{showProjectPhotos && <PhotosTab key={job.id} jobId={job.id} project={job} />}</details>}</div>}

        {active === "punch" && canProjectControl && <PunchListTab key={job.id} jobId={job.id} project={job} />}
        {active === "drawings" && canProjectControl && <DrawingsTab key={job.id} jobId={job.id} project={job} />}

        {active === "crew" && (
          <div className="space-y-3.5">
            <section className="hard-card p-5">
              <h2 className="font-semibold mb-2 flex items-center gap-2"><Users className="w-4 h-4" /> People · job access</h2>
              <p className="text-sm text-muted-foreground mb-3">Workers with access can view and use this job where their permissions allow. Access does not mean they are scheduled or on site.</p>
              {job.assigned_user_ids?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {job.assigned_user_ids.map((uid) => {
                    const u = userById(uid);
                    return (
                      <span key={uid} className="inline-flex items-center gap-1.5 bg-zinc-100 dark:bg-white/5 border border-zinc-300 dark:border-white/10 text-xs font-medium px-2 py-1 rounded-md" data-testid={`job-detail-crew-${uid}`}>
                        <Briefcase className="w-3 h-3 text-zinc-500" />{u?.name || uid.slice(0, 8)}
                        {u?.role && <span className="text-[11px] uppercase tracking-widest text-zinc-500 ml-1">{u.role}</span>}
                      </span>
                    );
                  })}
                </div>
              ) : <div className="text-sm text-zinc-400">No people have job access yet. Use Edit Job to assign access; plan dated allocations in People & Plant.</div>}
              {isOffice && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to={`/app/crew-planner?job_id=${job.id}`}><Button size="sm" variant="outline" className="h-8 rounded-lg text-[11px] font-bold uppercase" data-testid="job-crew-planner-link">People &amp; Plant</Button></Link>
                  <Link to={`/app/timesheets?job_id=${encodeURIComponent(job.id)}`}><Button size="sm" variant="outline" className="h-8 rounded-lg text-[11px] font-bold uppercase" data-testid="job-crew-timesheets-link">View timesheets</Button></Link>
                </div>
              )}
            </section>
            {isOffice && <><section className="space-y-2"><h2 className="font-semibold">People &amp; plant · allocations</h2><p className="text-sm text-muted-foreground">People and plant planned to work on this job by date.</p><JobSchedule jobId={job.id} /></section><section id="job-signin" tabIndex={-1} className="space-y-2 scroll-mt-24"><h2 className="font-semibold">Site sign-in / on site</h2><JobSigninPanel job={job} /></section></>}
          </div>
        )}

        {active === "costs" && isOffice && <JobCardBilling key={job.id} job={job} defaultExpanded />}
        {["costs", "commercial", "supplier-bills"].includes(active) && isOffice && <JobFinancialWorkspace job={job} initialSection={active === "commercial" ? "changes" : active === "supplier-bills" ? "supplier" : ""} />}

        {active === "documents" && (
          <div className="space-y-3.5">
            <section className="hard-card p-5">
              <div className="label-tag mb-2 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Plans & drawings</div>
              <p className="text-sm text-muted-foreground mb-3">Working plans and documents used to deliver this job.</p><Link className="inline-flex min-h-11 text-sm items-center text-orange-700 dark:text-orange-300 mb-2" to={jobLink("photos")}>View job photos →</Link>
              <JobPlans jobId={job.id} plans={job.plans || []} editable={isOffice} onChange={(next) => setJob((j) => ({ ...j, plans: next }))} />
            </section>
            {isOffice && <>
            <section id="job-commercial-files" tabIndex={-1} className="hard-card p-5 scroll-mt-24">
              <h2 className="font-semibold mb-2">Commercial documents</h2><p className="text-sm text-muted-foreground mb-3">Supporting quotes, variation files and invoice copies. Uploaded files are attachments, not live Fieldlink invoice records.</p>
              <JobDocuments jobId={job.id} />
            </section>
            </>}
            {isOffice && (
              <section id="job-completion" tabIndex={-1} className="hard-card p-5 flex items-center justify-between gap-3 flex-wrap scroll-mt-24" data-testid="job-completion-pack-card">
                <div>
                  <div className="label-tag mb-1 flex items-center gap-1.5"><FileDown className="w-3 h-3" /> Completion pack</div>
                  <p className="text-sm text-zinc-500">Generated closeout evidence: one PDF with the job's photos, dockets, forms and sign-offs.</p>
                </div>
                <Button onClick={downloadPack} disabled={packBusy} className="h-9 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold uppercase tracking-wider text-[11px]" data-testid="job-completion-pack-btn">
                  <FileDown className="w-3.5 h-3.5 mr-1.5" /> {packBusy ? "Building…" : "Download pack"}
                </Button>
              </section>
            )}
          </div>
        )}

        {active === "safety" && (
          <div className="space-y-3.5">
            <section className="space-y-3"><h2 className="font-semibold">Safety &amp; QA · today</h2><p className="text-sm text-muted-foreground">Review assigned forms and record site hazards or incidents.</p><div className="flex flex-wrap gap-2">{workflow.groups.find(group => group.label === "Safety")?.items.map(item => <Button key={item.key} asChild variant="outline" className="min-h-11"><Link to={item.to}>{item.key === "forms" ? "Complete form" : `Report ${item.label.toLowerCase()}`}</Link></Button>)}</div></section>
            <div id="job-forms" tabIndex={-1} className={section}><JobForms jobId={job.id} compactRequirements /></div>
            <section className="space-y-3"><h2 className="font-semibold">Site safety</h2><JobSSSP jobId={job.id} /><JobHazardousSubstances jobId={job.id} worker={!isOffice} /></section>
            <SiteAudit jobId={job.id} />
          </div>
        )}

        {active === "communications" && isOffice && (
          <div className="space-y-3.5">
            <JobCommunications job={job} />
            <ClientPortalCard job={job} />
          </div>
        )}

        {active === "activity" && <JobActivity jobId={job.id} />}
      </div>

      {unbilledWarn && (
        <Dialog open onOpenChange={(o) => !o && setUnbilledWarn(null)}>
          <DialogContent className="rounded-sm max-w-md" data-testid="job-unbilled-complete-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-5 h-5" /> This job still has unbilled work</DialogTitle>
              <DialogDescription>Review and invoice it now, or complete anyway and invoice later.</DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5 text-sm">
              {unbilledWarn.data.unbilled.labour > 0 && <div className="flex justify-between"><span>Labour</span><span className="font-mono font-bold">${unbilledWarn.data.unbilled.labour.toLocaleString("en-NZ")}</span></div>}
              {unbilledWarn.data.unbilled.plant > 0 && <div className="flex justify-between"><span>Plant</span><span className="font-mono font-bold">${unbilledWarn.data.unbilled.plant.toLocaleString("en-NZ")}</span></div>}
              {unbilledWarn.data.unbilled.materials > 0 && <div className="flex justify-between"><span>Materials</span><span className="font-mono font-bold">${unbilledWarn.data.unbilled.materials.toLocaleString("en-NZ")}</span></div>}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={forceComplete} data-testid="job-unbilled-complete-anyway">Complete With Unbilled Work</Button>
              <Button onClick={() => { setUnbilledWarn(null); nav(`/app/jobs/${id}/charge-up`); }} className="bg-[#FF5F15] hover:bg-[#E65100] text-white" data-testid="job-unbilled-review-invoice">Review & Invoice</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function Fact({ label, value, icon, mono }) {
  return (
    <div className="px-3 py-2">
      <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-zinc-500">{icon} {label}</div>
      <div className={`mt-0.5 ${mono ? "font-mono" : "font-display font-bold"} text-sm`}>{value}</div>
    </div>
  );
}
